import { motion } from 'framer-motion';
import { BookOpen, Users, Clock, Star, CheckCircle, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Cursos = () => {
  const cursos = [
    {
      id: 1,
      titulo: 'Astrología Básica',
      descripcion: 'Aprende los fundamentos de la astrología: signos, planetas, casas y aspectos. Curso completo para principiantes.',
      imagen: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f5?w=800&q=80',
      duracion: '8 semanas',
      estudiantes: 234,
      rating: 4.9,
      precio: 149,
      nivel: 'Principiante',
      temario: ['Signos del Zodíaco', 'Planetas y sus significados', 'Casas astrales', 'Aspectos planetarios'],
    },
    {
      id: 2,
      titulo: 'Lectura de Carta Natal',
      descripcion: 'Domina el arte de interpretar cartas natales. Análisis profundo de la personalidad y el destino.',
      imagen: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=800&q=80',
      duracion: '12 semanas',
      estudiantes: 189,
      rating: 4.8,
      precio: 249,
      nivel: 'Intermedio',
      temario: ['Construcción de la carta', 'Interpretación de aspectos', 'Análisis de casas', 'Síntesis integral'],
    },
    {
      id: 3,
      titulo: 'Tránsitos y Predicciones',
      descripcion: 'Aprende a predecir eventos futuros mediante el análisis de tránsitos planetarios y técnicas predictivas.',
      imagen: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=800&q=80',
      duracion: '10 semanas',
      estudiantes: 156,
      rating: 4.9,
      precio: 299,
      nivel: 'Avanzado',
      temario: ['Tránsitos planetarios', 'Revoluciones solares', 'Progresiones secundarias', 'Técnicas predictivas'],
    },
    {
      id: 4,
      titulo: 'Astrología y Relaciones',
      descripcion: 'Descubre la compatibilidad astrológica y las dinámicas de pareja a través de la sinastría.',
      imagen: 'https://images.unsplash.com/photo-1516339901601-2e1b62dc0c45?w=800&q=80',
      duracion: '6 semanas',
      estudiantes: 312,
      rating: 4.7,
      precio: 179,
      nivel: 'Intermedio',
      temario: ['Sinastría básica', 'Compatibilidad de signos', 'Aspectos en pareja', 'Carta compuesta'],
    },
    {
      id: 5,
      titulo: 'Luna y Emociones',
      descripcion: 'Explora la influencia de la Luna en nuestras emociones, necesidades y patrones emocionales.',
      imagen: 'https://images.unsplash.com/photo-1522030299830-16b8d3d049fe?w=800&q=80',
      duracion: '4 semanas',
      estudiantes: 278,
      rating: 4.8,
      precio: 99,
      nivel: 'Principiante',
      temario: ['La Luna en los signos', 'Fases lunares', 'Nodos lunares', 'Ciclos emocionales'],
    },
    {
      id: 6,
      titulo: 'Taller: Retorno Solar',
      descripcion: 'Taller intensivo para aprender a calcular e interpretar tu revolución solar anual.',
      imagen: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80',
      duracion: '2 días',
      estudiantes: 145,
      rating: 4.9,
      precio: 79,
      nivel: 'Todos',
      temario: ['Cálculo del retorno', 'Interpretación básica', 'Predicciones anuales', 'Ejercicios prácticos'],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
            <BookOpen className="w-4 h-4 mr-2" />
            Formación Astrológica
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Cursos y Talleres
          </h2>
          <p className="text-lg text-purple-100/60 max-w-2xl mx-auto">
            Descubre nuestros programas de formación diseñados para llevarte desde los fundamentos 
            hasta el dominio avanzado de la astrología.
          </p>
        </motion.div>

        {/* Cursos Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {cursos.map((curso) => (
            <motion.div key={curso.id} variants={itemVariants}>
              <Card className="group h-full bg-purple-950/40 border-purple-500/20 overflow-hidden hover:border-purple-400/40 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                {/* Imagen */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={curso.imagen}
                    alt={curso.titulo}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-950/80 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-purple-600/90 text-white border-0">
                    {curso.nivel}
                  </Badge>
                  <div className="absolute bottom-4 right-4 flex items-center gap-1 text-amber-300">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm font-medium">{curso.rating}</span>
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <h3 className="text-xl font-bold text-purple-100 group-hover:text-purple-300 transition-colors">
                    {curso.titulo}
                  </h3>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-sm text-purple-100/60 line-clamp-2">
                    {curso.descripcion}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-purple-200/50">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {curso.duracion}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {curso.estudiantes}
                    </span>
                  </div>

                  {/* Temario preview */}
                  <div className="space-y-1">
                    {curso.temario.slice(0, 3).map((tema, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-purple-200/70">
                        <CheckCircle className="w-3 h-3 text-purple-400" />
                        <span>{tema}</span>
                      </div>
                    ))}
                  </div>

                  {/* Precio y CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-purple-500/20">
                    <div>
                      <span className="text-2xl font-bold text-purple-300">${curso.precio}</span>
                      <span className="text-sm text-purple-200/50"> USD</span>
                    </div>
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-500 text-white"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Ver Curso
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-purple-200/60 mb-4">
            ¿No sabes por dónde empezar? Agenda una consulta gratuita
          </p>
          <Button
            variant="outline"
            size="lg"
            className="border-purple-400/30 text-purple-200 hover:bg-purple-500/10"
          >
            Asesoría Gratuita
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default Cursos;
